import { inject, Injectable, Type } from '@angular/core';
import {
  ModalController,
  ModalOptions as IonicModalOptions,
} from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { OverlayEventDetail } from '@ionic/core';
import { BaseModalComponent, BaseModalConfig } from 'src/app/modals/base-modal/base-modal.component';


// inherits from IonicModalOptions. Omit removes the original 'component'/'componentProps'
// to prevent TS error since we redefine them below with different types.
// component is normally NOT optional and componentProps is normally optional
export interface AppModalOptions
  extends Omit<IonicModalOptions, 'component' | 'componentProps'> {
  component?: Type<any>;
  componentProps: {
    config: Partial<BaseModalConfig>;
  };
  messageOverride?: ModalMessages;
}

export interface ModalMessages {
  message?: string;
  header?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  private modalController = inject(ModalController);
  private translate = inject(TranslateService);

  async showModal<T = any>(
    opts: AppModalOptions,
    message?: ModalMessages
  ): Promise<OverlayEventDetail<T>> {
    const inputConfig = opts.componentProps.config;

    let resolvedHeader: string | undefined;
    if (message?.header) {
      resolvedHeader = this.translate.instant(message.header);
    } else if (inputConfig.header) {
      resolvedHeader = this.translate.instant(inputConfig.header);
    } else {
      resolvedHeader = '';
    }

    let resolvedMessage: string | undefined;
    const messageSource = message?.message || inputConfig.message;

    if (messageSource) {
      resolvedMessage = this.translate.instant(messageSource);
    } else {
      resolvedMessage = '';
    }

    let resolvedFooterButtonText: string | undefined;
    const footerButtonTextKey = inputConfig.footerButtonText || 'BTN.OK';
    resolvedFooterButtonText = this.translate.instant(footerButtonTextKey);

     // the config obj for the component, putting the translated text in.
    const finalConfig: BaseModalConfig = {
      ...inputConfig,
      header: resolvedHeader,
      message: resolvedMessage,
      footerButtonText: resolvedFooterButtonText,
    };

    const finalComponent = opts.component || BaseModalComponent;

    // pulling out messageOverride etc. so baseIonicOptions is clean.
    // we also could spread directly opts and cast type any, but this is more type save
    const { messageOverride, component, componentProps, ...baseIonicOptions } =
      opts;

   //  build the options object for Ionic's create method (type IonicModalOptions).
    const ionicOptions: IonicModalOptions = {
      ...baseIonicOptions,
      component: finalComponent,
      componentProps: {
        config: finalConfig,
      },
    };

    if (await this.isModalOpen()) {
      await this.dismissModal().catch(() => {});
    }

    const modal = await this.modalController.create(ionicOptions);
    await modal.present();
    return modal.onDidDismiss() as Promise<OverlayEventDetail<T>>;
  }

  async isModalOpen() {
    return await this.modalController.getTop();
  }

  async dismissModal(data?: any, role?: string): Promise<boolean> {
    try {
      return await this.modalController.dismiss(data, role);
    } catch (e) {
      return false;
    }
  }

}
